import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { Row, SortingFn } from '@tanstack/react-table';

/**
 * Shared filter function for range-based column filters (e.g., price, stock quantity).
 * Handles both regular products and virtual parent rows (products with variants).
 *
 * For virtual parent rows, returns true if ANY variant matches the filter range.
 * For regular products, returns true if the product's value matches the filter range.
 *
 * @param row - The table row to filter
 * @param columnId - The column ID (field name) to filter on
 * @param filterValue - The filter value as [min, max] array
 * @returns true if the row should be shown, false otherwise
 */
export function rangeFilterFn(
  row: Row<DealItemWithVersions>,
  columnId: string,
  filterValue: unknown
): boolean {
  // Validate filterValue
  if (
    !Array.isArray(filterValue) ||
    filterValue.length < 2 ||
    typeof filterValue[0] !== 'number' ||
    typeof filterValue[1] !== 'number'
  ) {
    return true; // Show all if filter is invalid
  }

  // filterValue[0] and [1] are already validated as numbers, so we can use them directly
  const filterMin = filterValue[0];
  const filterMax = filterValue[1];

  // Check if this is a virtual parent row (has variants)
  const isVirtualParent = row.subRows.length > 1;

  if (isVirtualParent) {
    // Product has variants: check if ANY variant matches the filter
    const subRows =
      (
        row.original as DealItemWithVersions & {
          _subRows?: DealItemWithVersions[];
        }
      )._subRows || [];

    if (subRows.length === 1) {
      // Edge case: virtual parent with no variants - shouldn't happen but handle it
      return false;
    }
    return subRows.some((variant) => {
      const value = Number(
        variant.activeVersion?.[columnId as keyof DealItem] ?? 0
      );
      return !isNaN(value) && value >= filterMin && value <= filterMax;
    });
  } else {
    // Product without variants: check the product's own value
    const value = Number(
      row.original.activeVersion?.[columnId as keyof DealItem] ?? 0
    );
    return !isNaN(value) && value >= filterMin && value <= filterMax;
  }
}

/**
 * Creates a sorting function that handles grouped parent rows by falling back to subrows.
 * For grouped parent rows where getValue() returns undefined,
 * it falls back to the first subrow's value or the original row's value.
 *
 * @param columnId - The column ID to sort by
 * @param getValueFromRow - Function to extract the value from a row's original data
 * @param locale - Optional locale for locale-aware sorting (defaults to 'fr')
 * @returns A sorting function compatible with TanStack Table
 */
export function createSubRowSortingFn(
  columnId: string,
  getValueFromRow: (row: DealItemWithVersions) => string | null | undefined,
  locale: string = 'fr'
): SortingFn<DealItemWithVersions> {
  return (rowA, rowB) => {
    let a = rowA.getValue(columnId) as string | undefined;
    let b = rowB.getValue(columnId) as string | undefined;

    // For grouped parent rows, getValue returns undefined, so fallback to first subrow or original
    if (a === undefined) {
      const firstSubRowValue = rowA.subRows?.[0]?.original
        ? getValueFromRow(rowA.subRows[0].original)
        : undefined;
      const originalValue = rowA.original
        ? getValueFromRow(rowA.original)
        : undefined;
      a = firstSubRowValue ?? originalValue ?? '';
    }

    if (b === undefined) {
      const firstSubRowValue = rowB.subRows?.[0]?.original
        ? getValueFromRow(rowB.subRows[0].original)
        : undefined;
      const originalValue = rowB.original
        ? getValueFromRow(rowB.original)
        : undefined;
      b = firstSubRowValue ?? originalValue ?? '';
    }

    const aValue = String(a ?? '');
    const bValue = String(b ?? '');

    return aValue.localeCompare(bValue, locale, {
      sensitivity: 'base',
      numeric: true,
    });
  };
}
